import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import {
  buildVeeraContextualThreadKey,
  buildVeeraContextualTopicId,
  matchExistingVeeraThread,
  type VeeraContextualThreadInput,
  type VeeraContextualThreadRecord,
} from "@/lib/veeraContextualThreads";
import {
  createVeeraThreadStoreKey,
  persistVeeraDeletedThreadId,
  readPersistedVeeraDeletedThreadIds,
} from "@/lib/veeraThreads";

const EMPTY_PREVIEW_THREADS: never[] = [];

type VeeraThreadWithId = {
  id: string;
};

function applyContextualThreadMetadata<TThread extends VeeraThreadWithId>(
  thread: TThread,
  contextualThreadInput: VeeraContextualThreadInput | null | undefined,
): TThread {
  if (!contextualThreadInput) return thread;

  const existingThread = thread as TThread & Partial<VeeraContextualThreadRecord>;

  return {
    ...thread,
    threadKey: existingThread.threadKey ?? buildVeeraContextualThreadKey(contextualThreadInput),
    contextualTopicId: existingThread.contextualTopicId ?? buildVeeraContextualTopicId(contextualThreadInput),
    role: existingThread.role ?? contextualThreadInput.role,
    entrySurface: existingThread.entrySurface ?? contextualThreadInput.entrySurface,
    primaryEntityId: existingThread.primaryEntityId ?? contextualThreadInput.primaryEntityId,
    topicFingerprint: existingThread.topicFingerprint ?? contextualThreadInput.topicFingerprint,
    sourceId: existingThread.sourceId ?? contextualThreadInput.sourceId ?? null,
  } as TThread;
}

function buildInitialVeeraThreads<TThread extends VeeraThreadWithId>(
  createInitialThreads: (contextualThreadInput?: VeeraContextualThreadInput) => TThread[],
  contextualThreadInput: VeeraContextualThreadInput | null | undefined,
) {
  const initialThreads = createInitialThreads(contextualThreadInput ?? undefined);

  if (!contextualThreadInput || initialThreads.length === 0) {
    return initialThreads;
  }

  const [firstThread, ...restThreads] = initialThreads;
  return [
    applyContextualThreadMetadata(firstThread, contextualThreadInput),
    ...restThreads,
  ];
}

interface UseVeeraThreadsOptions<TThread extends VeeraThreadWithId> {
  surface: "coach" | "leader";
  userId?: string;
  isPreview?: boolean;
  previewThreads?: TThread[];
  contextualThreadInput?: VeeraContextualThreadInput | null;
  createInitialThreads: (contextualThreadInput?: VeeraContextualThreadInput) => TThread[];
}

interface UseVeeraThreadsResult<TThread extends VeeraThreadWithId> {
  threads: TThread[];
  setThreads: Dispatch<SetStateAction<TThread[]>>;
  activeThreadId: string | null;
  setActiveThreadId: Dispatch<SetStateAction<string | null>>;
  storageKey: string;
  hideThread: (threadId: string) => void;
}

export function useVeeraThreads<TThread extends VeeraThreadWithId>({
  surface,
  userId,
  isPreview = false,
  previewThreads,
  contextualThreadInput,
  createInitialThreads,
}: UseVeeraThreadsOptions<TThread>): UseVeeraThreadsResult<TThread> {
  const storageKey = useMemo(
    () => createVeeraThreadStoreKey(surface, userId),
    [surface, userId],
  );
  const resolvedPreviewThreads = useMemo(
    () => previewThreads ?? (EMPTY_PREVIEW_THREADS as TThread[]),
    [previewThreads],
  );
  const contextualThreadInputKey = useMemo(() => (
    contextualThreadInput ? buildVeeraContextualThreadKey(contextualThreadInput) : null
  ), [contextualThreadInput]);
  const [threads, setThreads] = useState<TThread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [hasBootstrapped, setHasBootstrapped] = useState(false);
  const handledContextualThreadKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setThreads([]);
      setActiveThreadId(null);
      setHasBootstrapped(false);
      handledContextualThreadKeyRef.current = null;
      return;
    }

      const deletedThreadIds = readPersistedVeeraDeletedThreadIds(surface, userId);
      const filterDeletedThreads = (candidateThreads: TThread[]) => candidateThreads.filter(
        (thread) => !deletedThreadIds.includes(thread.id),
      );

      const applyThreads = (nextThreads: TThread[], nextActiveThreadId?: string | null) => {
        setThreads(nextThreads);
      setActiveThreadId((current) => {
        if (nextActiveThreadId) {
          return nextThreads.some((thread) => thread.id === nextActiveThreadId)
            ? nextActiveThreadId
            : nextThreads[0]?.id ?? null;
        }

        return current && nextThreads.some((thread) => thread.id === current)
          ? current
          : nextThreads[0]?.id ?? null;
      });
      setHasBootstrapped(true);
    };

    if (isPreview) {
      applyThreads(resolvedPreviewThreads);
      return;
    }

    const resolvedContextualThreadInput = contextualThreadInput ?? null;
    handledContextualThreadKeyRef.current = null;
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      const initialThreads = filterDeletedThreads(buildInitialVeeraThreads(createInitialThreads, resolvedContextualThreadInput));
      applyThreads(initialThreads, resolvedContextualThreadInput ? initialThreads[0]?.id ?? null : undefined);
      if (contextualThreadInputKey) {
        handledContextualThreadKeyRef.current = contextualThreadInputKey;
      }
      window.localStorage.setItem(storageKey, JSON.stringify(initialThreads));
      return;
    }

    try {
      const parsed = filterDeletedThreads(JSON.parse(raw) as TThread[]);
      if (!Array.isArray(parsed) || parsed.length === 0) {
        throw new Error("Invalid thread payload");
      }

      if (resolvedContextualThreadInput) {
        const match = matchExistingVeeraThread({
          threads: parsed as unknown as VeeraContextualThreadRecord[],
          candidate: resolvedContextualThreadInput,
        });

        if (match.thread) {
          applyThreads(parsed, match.thread.id);
          if (contextualThreadInputKey) {
            handledContextualThreadKeyRef.current = contextualThreadInputKey;
          }
          return;
        }

        const newThreads = filterDeletedThreads(buildInitialVeeraThreads(createInitialThreads, resolvedContextualThreadInput));
        const mergedThreads = [...newThreads, ...parsed];
        applyThreads(mergedThreads, newThreads[0]?.id ?? mergedThreads[0]?.id ?? null);
        if (contextualThreadInputKey) {
          handledContextualThreadKeyRef.current = contextualThreadInputKey;
        }
        window.localStorage.setItem(storageKey, JSON.stringify(mergedThreads));
        return;
      }

      applyThreads(parsed);
    } catch {
      const initialThreads = filterDeletedThreads(buildInitialVeeraThreads(createInitialThreads, resolvedContextualThreadInput));
      applyThreads(initialThreads, resolvedContextualThreadInput ? initialThreads[0]?.id ?? null : undefined);
      if (contextualThreadInputKey) {
        handledContextualThreadKeyRef.current = contextualThreadInputKey;
      }
      window.localStorage.setItem(storageKey, JSON.stringify(initialThreads));
    }
  }, [contextualThreadInputKey, createInitialThreads, isPreview, resolvedPreviewThreads, storageKey, userId]);

  useEffect(() => {
    if (!userId || isPreview || !hasBootstrapped) return;
    window.localStorage.setItem(storageKey, JSON.stringify(threads));
  }, [hasBootstrapped, isPreview, storageKey, threads, userId]);

  useEffect(() => {
    if (!userId || isPreview || !hasBootstrapped || !contextualThreadInput) return;
    if (handledContextualThreadKeyRef.current === contextualThreadInputKey) return;

    const match = matchExistingVeeraThread({
      threads: threads as unknown as VeeraContextualThreadRecord[],
      candidate: contextualThreadInput,
    });

    if (match.thread) {
      handledContextualThreadKeyRef.current = contextualThreadInputKey;
      if (activeThreadId !== match.thread.id) {
        setActiveThreadId(match.thread.id);
      }
      return;
    }

    const nextThreads = buildInitialVeeraThreads(createInitialThreads, contextualThreadInput);
    const nextThread = nextThreads[0];
    if (!nextThread) return;

    if (threads.some((thread) => thread.id === nextThread.id)) {
      if (activeThreadId !== nextThread.id) {
        setActiveThreadId(nextThread.id);
      }
      return;
    }

    setThreads((current) => [...nextThreads, ...current]);
    handledContextualThreadKeyRef.current = contextualThreadInputKey;
    setActiveThreadId(nextThread.id);
  }, [
    surface,
    activeThreadId,
    contextualThreadInput,
    contextualThreadInputKey,
    createInitialThreads,
    hasBootstrapped,
    isPreview,
    threads,
    userId,
  ]);

  return {
    threads,
    setThreads,
    activeThreadId,
    setActiveThreadId,
    storageKey,
    hideThread: (threadId: string) => {
      if (!userId || isPreview) return;
      persistVeeraDeletedThreadId(surface, userId, threadId);
    },
  };
}
